import { useState, useEffect, useRef } from 'react';
import { useApp } from '../App';
import api from '../api';
import { useI18n } from '../i18n/index.jsx';

const CURRENCIES = ['PLN', 'EUR', 'USD', 'GBP', 'UAH', 'CZK'];

const currencyFlags = { PLN: '🇵🇱', EUR: '🇪🇺', USD: '🇺🇸', GBP: '🇬🇧', UAH: '🇺🇦', CZK: '🇨🇿' };
const currencySymbols = { PLN: 'zł', EUR: '€', USD: '$', GBP: '£', UAH: '₴', CZK: 'Kč' };

function currencyFlag(c) { return currencyFlags[c] || '🏳️'; }

function getCardGradient(currency) {
  const map = {
    PLN: 'linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%)',
    EUR: 'linear-gradient(135deg, #1a3a2a 0%, #059669 100%)',
    USD: 'linear-gradient(135deg, #3a1a3a 0%, #9333ea 100%)',
    GBP: 'linear-gradient(135deg, #3a2a1a 0%, #d97706 100%)',
    UAH: 'linear-gradient(135deg, #1a2a3a 0%, #0ea5e9 100%)',
    CZK: 'linear-gradient(135deg, #2a1a1a 0%, #dc2626 100%)',
  };
  return map[currency] || 'linear-gradient(135deg, #2a2a3a 0%, #6366f1 100%)';
}

function formatBalance(val, currency) {
  const num = Number(val) || 0;
  const parts = num.toFixed(2).split('.');
  const intPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  return `${intPart},${parts[1]}`;
}

const inputStyle = {
  width: '100%', padding: '14px 16px', borderRadius: 12, fontSize: 15,
  border: '1px solid rgba(255,255,255,0.12)',
  background: 'rgba(255,255,255,0.07)', color: '#fff',
  outline: 'none', boxSizing: 'border-box',
};
const selectStyle = { ...inputStyle, appearance: 'auto' };
const labelStyle = {
  display: 'block', fontSize: 13, color: 'rgba(255,255,255,0.7)',
  marginBottom: 6, fontWeight: 700,
};

export default function BankAccounts() {
  const { setPage } = useApp();
  const { t } = useI18n();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [editingBalance, setEditingBalance] = useState(null);
  const [balanceInput, setBalanceInput] = useState('');
  const [form, setForm] = useState({ bank_name: '', iban: '', swift: '', currency: 'PLN', is_default: false, balance: 0 });
  const scrollRef = useRef(null);

  useEffect(() => { load(); }, []);

  async function load() {
    try {
      const { data } = await api.get('/my/bank-accounts');
      setAccounts(data || []);
    } catch {}
    setLoading(false);
  }

  function resetForm() {
    setForm({ bank_name: '', iban: '', swift: '', currency: 'PLN', is_default: false, balance: 0 });
    setEditId(null);
    setShowForm(false);
  }

  async function save() {
    try {
      if (editId) {
        await api.put(`/my/bank-accounts/${editId}`, form);
      } else {
        await api.post('/my/bank-accounts', { ...form, company_id: 0 });
      }
      resetForm();
      load();
    } catch (e) {
      alert(e?.response?.data?.detail || t('common.error'));
    }
  }

  async function remove(id) {
    if (!confirm(t('bank_accounts.confirm_delete'))) return;
    try {
      await api.delete(`/my/bank-accounts/${id}`);
      setSelectedAccount(null);
      load();
    } catch {}
  }

  async function setDefault(id) {
    try {
      await api.put(`/my/bank-accounts/${id}`, { is_default: true });
      load();
    } catch {}
  }

  function startEdit(acc) {
    setForm({ bank_name: acc.bank_name || '', iban: acc.iban || '', swift: acc.swift || '', currency: acc.currency, is_default: acc.is_default, balance: acc.balance || 0 });
    setEditId(acc.id);
    setSelectedAccount(null);
    setShowForm(true);
  }

  async function saveBalance(accId) {
    try {
      await api.put(`/my/bank-accounts/${accId}/balance`, { balance: parseFloat(balanceInput) || 0 });
      setEditingBalance(null);
      load();
    } catch {}
  }

  function formatIban(iban) {
    if (!iban) return '';
    return iban.replace(/(.{4})/g, '$1 ').trim();
  }

  function copyToClipboard(text) {
    navigator.clipboard.writeText(text).then(() => {
      // brief visual feedback could be added
    }).catch(() => {});
  }

  const totalBalance = accounts.reduce((sum, acc) => sum + (Number(acc.balance) || 0), 0);

  if (loading) return <div className="page"><p style={{ textAlign: 'center', opacity: 0.5, padding: 40 }}>{t('common.loading')}</p></div>;

  // Detail view for selected account
  if (selectedAccount) {
    const acc = accounts.find(a => a.id === selectedAccount);
    if (!acc) { setSelectedAccount(null); return null; }
    return (
      <div className="page" style={{ padding: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
          <button onClick={() => setSelectedAccount(null)} style={{
            background: 'rgba(255,255,255,0.1)', border: 'none', color: '#fff',
            borderRadius: 12, padding: '8px 14px', fontSize: 18, cursor: 'pointer',
          }}>←</button>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800 }}>{acc.bank_name || t('bank_accounts.account')}</h2>
        </div>

        {/* Card preview */}
        <div style={{
          background: getCardGradient(acc.currency), borderRadius: 20,
          padding: '24px 22px', marginBottom: 20, color: '#fff',
        }}>
          <div style={{ fontSize: 13, opacity: 0.8 }}>{currencyFlag(acc.currency)} {acc.currency}</div>
          <div style={{ fontSize: 36, fontWeight: 800, margin: '10px 0', letterSpacing: -1 }}>
            {formatBalance(acc.balance, acc.currency)} <span style={{ fontSize: 18, opacity: 0.7 }}>{currencySymbols[acc.currency] || acc.currency}</span>
          </div>
          <div style={{ fontFamily: 'monospace', fontSize: 13, opacity: 0.7, letterSpacing: 2 }}>
            {formatIban(acc.iban)}
          </div>
        </div>

        {/* Details */}
        <div style={{
          background: 'rgba(255,255,255,0.04)', borderRadius: 16,
          padding: '18px 16px', border: '1px solid rgba(255,255,255,0.08)', marginBottom: 16,
        }}>
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 12, opacity: 0.5, marginBottom: 4 }}>IBAN</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontFamily: 'monospace', fontSize: 14, flex: 1, wordBreak: 'break-all' }}>{formatIban(acc.iban)}</span>
              <button onClick={() => copyToClipboard(acc.iban)} style={{
                background: 'rgba(255,255,255,0.1)', border: 'none', color: '#fff',
                borderRadius: 8, padding: '6px 10px', cursor: 'pointer', fontSize: 14,
              }}>📋</button>
            </div>
          </div>
          {acc.swift && (
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 12, opacity: 0.5, marginBottom: 4 }}>SWIFT/BIC</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontFamily: 'monospace', fontSize: 14, flex: 1 }}>{acc.swift}</span>
                <button onClick={() => copyToClipboard(acc.swift)} style={{
                  background: 'rgba(255,255,255,0.1)', border: 'none', color: '#fff',
                  borderRadius: 8, padding: '6px 10px', cursor: 'pointer', fontSize: 14,
                }}>📋</button>
              </div>
            </div>
          )}
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 12, opacity: 0.5, marginBottom: 4 }}>{t('bank_accounts.bank')}</div>
            <div style={{ fontSize: 14 }}>{acc.bank_name || '—'}</div>
          </div>
          <div>
            <div style={{ fontSize: 12, opacity: 0.5, marginBottom: 4 }}>{t('bank_accounts.balance_label')}</div>
            {editingBalance === acc.id ? (
              <div style={{ display: 'flex', gap: 8 }}>
                <input style={{ ...inputStyle, flex: 1, padding: '10px 14px' }}
                  type="number" step="0.01" value={balanceInput}
                  onChange={e => setBalanceInput(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && saveBalance(acc.id)}
                  autoFocus />
                <button onClick={() => saveBalance(acc.id)} style={{
                  background: '#10b981', border: 'none', color: '#fff',
                  borderRadius: 10, padding: '10px 16px', cursor: 'pointer', fontWeight: 700,
                }}>✓</button>
                <button onClick={() => setEditingBalance(null)} style={{
                  background: 'rgba(255,255,255,0.1)', border: 'none', color: '#fff',
                  borderRadius: 10, padding: '10px 14px', cursor: 'pointer',
                }}>✕</button>
              </div>
            ) : (
              <div onClick={() => { setEditingBalance(acc.id); setBalanceInput(String(acc.balance || 0)); }}
                style={{ fontSize: 20, fontWeight: 800, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}>
                {formatBalance(acc.balance, acc.currency)} {currencySymbols[acc.currency] || acc.currency}
                <span style={{ fontSize: 14, opacity: 0.4 }}>✏️</span>
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={() => startEdit(acc)} style={{
            flex: 1, padding: 14, fontSize: 15, fontWeight: 700, borderRadius: 12,
            background: 'rgba(99,102,241,0.2)', border: '1px solid rgba(99,102,241,0.3)',
            color: '#818cf8', cursor: 'pointer',
          }}>✏️ {t('bank_accounts.edit_btn')}</button>
          {!acc.is_default && (
            <button onClick={() => { setDefault(acc.id); setSelectedAccount(null); }} style={{
              flex: 1, padding: 14, fontSize: 15, fontWeight: 700, borderRadius: 12,
              background: 'rgba(16,185,129,0.2)', border: '1px solid rgba(16,185,129,0.3)',
              color: '#10b981', cursor: 'pointer',
            }}>⭐ {t('bank_accounts.set_default')}</button>
          )}
          <button onClick={() => remove(acc.id)} style={{
            padding: 14, fontSize: 15, borderRadius: 12,
            background: 'rgba(239,68,68,0.2)', border: '1px solid rgba(239,68,68,0.3)',
            color: '#ef4444', cursor: 'pointer',
          }}>🗑</button>
        </div>
      </div>
    );
  }

  return (
    <div className="page" style={{ padding: '16px', position: 'relative', minHeight: '100vh' }}>

      {/* Total balance header */}
      <div style={{
        background: 'linear-gradient(135deg, #1a1a3e 0%, #2d1b69 50%, #1a1a3e 100%)',
        borderRadius: 20, padding: '28px 24px', marginBottom: 20,
        position: 'relative', overflow: 'hidden', color: '#fff',
      }}>
        <div style={{
          position: 'absolute', top: -40, right: -40, width: 150, height: 150,
          background: 'rgba(255,255,255,0.03)', borderRadius: '50%',
        }} />
        <div style={{
          position: 'absolute', bottom: -30, left: -30, width: 100, height: 100,
          background: 'rgba(255,255,255,0.02)', borderRadius: '50%',
        }} />
        <div style={{ fontSize: 13, opacity: 0.7, marginBottom: 4 }}>💰 {t('bank_accounts.total_balance')}</div>
        <div style={{ fontSize: 36, fontWeight: 800, letterSpacing: -1 }}>
          {formatBalance(totalBalance, 'PLN')} <span style={{ fontSize: 18, opacity: 0.7 }}>PLN</span>
        </div>
        <div style={{ fontSize: 13, opacity: 0.5, marginTop: 8 }}>
          {accounts.length} {accounts.length === 1 ? t('bank_accounts.accounts_count_1') : accounts.length < 5 ? t('bank_accounts.accounts_count_2_4') : t('bank_accounts.accounts_count_5plus')}
        </div>
      </div>

      {/* Account cards - horizontal scroll */}
      {accounts.length > 0 && (
        <div ref={scrollRef} style={{
          display: 'flex', gap: 14, overflowX: 'auto', paddingBottom: 14,
          WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none', marginBottom: 6,
        }}>
          {accounts.map(acc => (
            <div key={acc.id} onClick={() => setSelectedAccount(acc.id)} style={{
              minWidth: 220, background: getCardGradient(acc.currency),
              borderRadius: 16, padding: '20px 18px', flexShrink: 0,
              position: 'relative', overflow: 'hidden', cursor: 'pointer',
              color: '#fff', transition: 'transform 0.15s',
              border: acc.is_default ? '2px solid rgba(255,255,255,0.3)' : '2px solid transparent',
            }}>
              <div style={{
                position: 'absolute', top: -20, right: -20, width: 80, height: 80,
                background: 'rgba(255,255,255,0.06)', borderRadius: '50%',
              }} />
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ fontSize: 12, opacity: 0.8 }}>{currencyFlag(acc.currency)} {acc.currency}</div>
                {acc.is_default && <div style={{ fontSize: 10, background: 'rgba(255,255,255,0.2)', padding: '2px 8px', borderRadius: 8 }}>{t('bank_accounts.main')}</div>}
              </div>
              <div style={{ fontSize: 28, fontWeight: 800, margin: '10px 0', letterSpacing: -0.5 }}>
                {formatBalance(acc.balance, acc.currency)}
              </div>
              <div style={{ fontFamily: 'monospace', fontSize: 11, opacity: 0.6, letterSpacing: 2 }}>
                •••• •••• {acc.iban?.slice(-4) || '0000'}
              </div>
              <div style={{ fontSize: 12, opacity: 0.7, marginTop: 6 }}>{acc.bank_name || ''}</div>
            </div>
          ))}
        </div>
      )}

      {/* Quick actions */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 24,
      }}>
        {[
          { icon: '💸', label: t('bank_accounts.transfer') },
          { icon: '📥', label: t('bank_accounts.top_up') },
          { icon: '💱', label: t('bank_accounts.exchange') },
          { icon: '📄', label: t('bank_accounts.invoice'), action: () => setPage('invoices') },
        ].map(btn => (
          <button key={btn.label} onClick={btn.action || (() => {})} style={{
            background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)',
            borderRadius: 14, padding: '14px 6px', display: 'flex', flexDirection: 'column',
            alignItems: 'center', gap: 6, cursor: 'pointer', color: '#fff',
          }}>
            <span style={{ fontSize: 22 }}>{btn.icon}</span>
            <span style={{ fontSize: 11, opacity: 0.7 }}>{btn.label}</span>
          </button>
        ))}
      </div>

      {/* Empty state */}
      {accounts.length === 0 && !showForm && (
        <div style={{
          textAlign: 'center', padding: '48px 20px',
          background: 'rgba(255,255,255,0.04)', borderRadius: 16,
          border: '1px solid rgba(255,255,255,0.08)',
        }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>🏦</div>
          <p style={{ opacity: 0.6, fontSize: 15, marginBottom: 4 }}>{t('bank_accounts.no_bank_accounts')}</p>
          <p style={{ fontSize: 13, opacity: 0.4 }}>{t('bank_accounts.add_for_invoices')}</p>
        </div>
      )}

      {/* Account list (compact) */}
      {accounts.length > 0 && (
        <div>
          <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 12, opacity: 0.8 }}>{t('bank_accounts.all_accounts')}</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {accounts.map(acc => (
              <div key={acc.id} onClick={() => setSelectedAccount(acc.id)} style={{
                background: 'rgba(255,255,255,0.04)', borderRadius: 14,
                padding: '14px 16px', border: '1px solid rgba(255,255,255,0.06)',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                cursor: 'pointer',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{
                    width: 40, height: 40, borderRadius: 12, background: getCardGradient(acc.currency),
                    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18,
                  }}>{currencyFlag(acc.currency)}</div>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 600 }}>{acc.bank_name || t('bank_accounts.account')}</div>
                    <div style={{ fontSize: 12, opacity: 0.5 }}>•••• {acc.iban?.slice(-4) || '0000'}</div>
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 15, fontWeight: 700 }}>{formatBalance(acc.balance, acc.currency)}</div>
                  <div style={{ fontSize: 11, opacity: 0.5 }}>{acc.currency}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Add account form */}
      {showForm && (
        <div style={{
          background: 'rgba(255,255,255,0.04)', borderRadius: 16,
          padding: '18px 16px', border: '1px solid rgba(255,255,255,0.08)',
          marginTop: 20,
        }}>
          <h3 style={{ margin: '0 0 16px 0', fontSize: 17, fontWeight: 800 }}>
            {editId ? `✏️ ${t('bank_accounts.edit_account')}` : `➕ ${t('bank_accounts.new_account')}`}
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div>
              <label style={labelStyle}>{t('bank_accounts.bank_name_label')}</label>
              <input style={inputStyle} placeholder="напр. mBank, PKO BP, ING..."
                value={form.bank_name} onChange={e => setForm({ ...form, bank_name: e.target.value })} />
            </div>
            <div>
              <label style={labelStyle}>{t('bank_accounts.iban_label')}</label>
              <input style={inputStyle} placeholder="PL00 0000 0000 0000 0000 0000 0000"
                value={form.iban} onChange={e => setForm({ ...form, iban: e.target.value })} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <label style={labelStyle}>SWIFT/BIC</label>
                <input style={inputStyle} placeholder="BREXPLPW"
                  value={form.swift} onChange={e => setForm({ ...form, swift: e.target.value })} />
              </div>
              <div>
                <label style={labelStyle}>{t('bank_accounts.currency_label')}</label>
                <select style={selectStyle} value={form.currency}
                  onChange={e => setForm({ ...form, currency: e.target.value })}>
                  {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label style={labelStyle}>{t('bank_accounts.balance_label')}</label>
              <input style={inputStyle} type="number" step="0.01" placeholder="0.00"
                value={form.balance} onChange={e => setForm({ ...form, balance: parseFloat(e.target.value) || 0 })} />
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', padding: '8px 0' }}>
              <input type="checkbox" checked={form.is_default}
                onChange={e => setForm({ ...form, is_default: e.target.checked })}
                style={{ width: 20, height: 20, accentColor: '#10b981' }} />
              <span style={{ fontSize: 14, color: 'rgba(255,255,255,0.8)' }}>{t('bank_accounts.default_checkbox')}</span>
            </label>
          </div>
          <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
            <button className="btn btn-primary" onClick={save}
              style={{ flex: 1, padding: 14, fontSize: 15, fontWeight: 700, borderRadius: 12 }}>
              {editId ? `💾 ${t('bank_accounts.save_btn')}` : `✅ ${t('bank_accounts.add_btn')}`}
            </button>
            <button onClick={resetForm} style={{
              flex: 1, padding: 14, fontSize: 15, borderRadius: 12, cursor: 'pointer',
              border: '1px solid rgba(255,255,255,0.15)', background: 'transparent',
              color: 'rgba(255,255,255,0.7)',
            }}>{t('common.cancel')}</button>
          </div>
        </div>
      )}

      {/* Floating add button */}
      {!showForm && (
        <button onClick={() => { resetForm(); setShowForm(true); }} style={{
          position: 'fixed', bottom: 80, right: 20, width: 56, height: 56,
          borderRadius: '50%', background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
          border: 'none', color: '#fff', fontSize: 28, cursor: 'pointer',
          boxShadow: '0 4px 20px rgba(99,102,241,0.4)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', zIndex: 10,
        }}>+</button>
      )}
    </div>
  );
}
